import type { Track } from 'livekit-client';

/** Which stream the always-on-top PiP widget is currently mirroring. */
export type PipSource = 'remoteScreen' | 'remoteCam' | 'localScreen' | 'localCam';

export type PipBinding = { source: PipSource; mirror: boolean };

export type PipCandidates = {
  remoteScreen: Track | null;
  remoteCamera: Track | null;
  localScreen: Track | null;
  localCamera: Track | null;
};

/** A track worth showing: publishing right now, not muted, not ended. */
export const isLiveTrack = (track: Track | null): boolean =>
  Boolean(track && !track.isMuted && track.mediaStreamTrack.readyState === 'live');

/**
 * The single stream the PiP widget shows, in the same priority order as the
 * in-app stage: peer screen > peer camera > my screen > my camera. Checking the
 * track (muted / ended) rather than a React flag matters — a camera the peer just
 * turned off keeps its MediaStream, and going by the stream alone would paint a
 * black rectangle where the avatar belongs.
 */
export const pickPipSpotlight = (
  candidates: PipCandidates
): { track: Track; binding: PipBinding } | null => {
  const ordered: { track: Track | null; binding: PipBinding }[] = [
    { track: candidates.remoteScreen, binding: { source: 'remoteScreen', mirror: false } },
    { track: candidates.remoteCamera, binding: { source: 'remoteCam', mirror: false } },
    { track: candidates.localScreen, binding: { source: 'localScreen', mirror: false } },
    // Own camera reads as a mirror; screens and the peer's camera do not.
    { track: candidates.localCamera, binding: { source: 'localCam', mirror: true } },
  ];
  const match = ordered.find((candidate) => isLiveTrack(candidate.track));
  return match?.track ? { track: match.track, binding: match.binding } : null;
};
